import {
  FileFetcher,
  ImportableFile,
  ImportProgress,
  ImportRunResult,
  RunTarget,
} from 'helpers/import-types';
import { removeFileExtension } from 'helpers/file-utils';
import { isOutOfStorageError } from 'helpers/storage';
import { uploadBlobToS3 } from 'helpers/upload-blob-to-s3';
import { VaultsController } from 'networking/controllers/vaults-controller';

type RunOptions<F extends ImportableFile> = {
  vaultId: number;
  targets: RunTarget<F>[];
  fetchFile: FileFetcher<F>;
  onProgress?: (progress: ImportProgress) => void;
};

/**
 * Imports reviewed files for every source (computer, Drive, Dropbox, iCloud):
 * creates each new album with its date range, then uploads its files one by
 * one and dates each memory. Files with no album go to the vault's default
 * album, i.e. they are unsorted memories.
 */
export const runImport = async <F extends ImportableFile>({
  vaultId,
  targets,
  fetchFile,
  onProgress,
}: RunOptions<F>): Promise<ImportRunResult> => {
  let albumsCreated = 0;
  let filesCreated = 0;
  let filesFailed = 0;

  for (let t = 0; t < targets.length; t += 1) {
    const target = targets[t];
    let albumId: number | undefined;

    if (target.kind === 'new') {
      try {
        // eslint-disable-next-line no-await-in-loop
        const album = await VaultsController.createAlbum(vaultId, target.name, {
          startDate: target.startDate,
          endDate: target.endDate,
        });
        albumId = album.id;
        albumsCreated += 1;
      } catch {
        filesFailed += target.files.length;
        continue; // eslint-disable-line no-continue
      }
    } else if (target.kind === 'existing') {
      albumId = target.albumId;
    }

    for (let f = 0; f < target.files.length; f += 1) {
      const { item, date } = target.files[f];
      onProgress?.({
        albumIndex: t,
        albumTotal: targets.length,
        albumName: target.name,
        fileIndex: f,
        fileTotal: target.files.length,
        fileName: item.name,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        const file = await fetchFile(item);
        // eslint-disable-next-line no-await-in-loop
        const { signedId } = await uploadBlobToS3(file);
        // eslint-disable-next-line no-await-in-loop
        const created = await VaultsController.createDocument(vaultId, {
          clip: {
            videoSignedId: signedId,
            name: removeFileExtension(item.name),
            recordedAt: new Date(),
            isPrivate: false,
            albumId,
          },
        });

        // The date isn't accepted on create. Start and end are both set, as
        // the app's own date editor does for a single day.
        if (date && created?.id) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await VaultsController.updateContentMetadata(created.id, {
              startDate: date,
              endDate: date,
            });
          } catch {
            // Non-fatal; the memory is created, just undated.
          }
        }
        filesCreated += 1;
      } catch (err) {
        // A full vault fails every remaining file the same way: stop with a
        // clear message instead of grinding through them.
        if (isOutOfStorageError(err)) {
          throw new Error(
            "You're out of storage — the import was stopped. " +
              `${filesCreated} ${filesCreated === 1 ? 'file was' : 'files were'} imported before the limit was reached.`,
          );
        }
        // eslint-disable-next-line no-console
        console.error('[import] file failed', {
          file: item.name,
          mimeType: item.mimeType,
          message: err instanceof Error ? err.message : String(err),
        });
        filesFailed += 1;
      }
    }
  }

  return { albumsCreated, filesCreated, filesFailed };
};
